let qid = 0;

export class PromiseQueue {
  constructor (debug = false, readTimeout = (2 * 60 * 1000), writeTimeout = (15 * 60 * 1000), parent = null) {
    this.debug = debug;
    this.readTimeout = readTimeout;
    this.writeTimeout = writeTimeout;
    this.parent = parent;
    if (debug) {
      this._qid = (parent ? (`${parent._qid}.`) : '') + (qid++);
      this._opid = 0;
    }
    this.lastWrite = Promise.resolve(undefined);
    this.lastReads = [];
    this._subqueues = {};
    this._locked = false;
  }
  lock () {
    this._locked = true;
  }
  /**
   * Returns a promise that will resolve when the given side-effect-having
   * operation completes. These operations are guaranteed to execute in the
   * order they were added, never concurrently.
   *
   * @param  {Function} fn  The operation to execute. It receives as an
   * argument the value of the previous write op (if available) and its return
   * value is passed on to the next write op.
   *
   * @return {Promise}       Resolves with the value returned by `fn`.
   */
  writeOp (fn) {
    let opid = false;
    if (this.debug) {
      opid = this._nextOpId();
      console.log('+++ w', opid);
    }
    let execFn = this._makeExecFn(fn, 'w', opid);

    let thisWrite;
    if (this.currentOpExec) {
      // operations fired during operation exec should start in their own subqueue (or they'll deadlock)

      // @TODO: see note in readOp

      let [opid, mode] = this.currentOpExec;
      if (mode === 'r') {
        throw new Error('No writing inside read operations!');
      }
      thisWrite = this.subqueueFor(opid).writeOp(fn);
    } else {
      if (this.lastReads.length > 0 ) {
        let lastReads = this.lastReads;
        this.lastReads = [];
        thisWrite = this.lastWrite.then(
          result => Promise.all(lastReads).then(() => execFn(result))
        );
      } else {
        thisWrite = this.lastWrite.then(execFn);
      }
      this.lastWrite = thisWrite;
    }

    if (this.debug) {
      thisWrite._opid = opid;
    }

    return thisWrite;
  }


  /**
   * Returns a promise that will resolve with the return value of the
   * side-effect-free operation given. This operation is guaranteed to execute
   * only after any *write* operations that were pending at the time of the
   * call finished resolving.
   *
   * @param  {Function} fn The read operation to execute. It receives as an
   * argument the value of the previous write operation (if available).
   *
   * @return {Promise}      Resolves with the value returned by `fn`.
   */
  readOp (fn) {
    if (this.debug) {
      var opid = this._nextOpId();
      console.log('+++ r', opid);
    }
    let execFn = this._makeExecFn(fn, 'r', opid);
    let thisRead;
    if (this.currentOpExec) {
      // operations fired during operation exec should start in their own subqueue (or they'll deadlock)

      // @TODO: followup reads after the operation finishes starting but
      // before it resolves completely are an IRRESOLVABLE DEADLOCK
      // until we switch to explicit queue operations!

      let [opid, mode] = this.currentOpExec;
      thisRead = this.subqueueFor(opid).readOp(fn);
    } else {
      thisRead = this.lastWrite.then(execFn);
      this.lastReads.push(thisRead);
    }

    if (this.debug) {
      thisRead._opid = opid;
    }
    return thisRead;
  }

  subqueueFor (opid) {
    if ( !(opid in this._subqueues) ) {
      this._subqueues[opid] = new PromiseQueue(this.debug, this.readTimeout, this.writeTimeout, this);
    }
    return this._subqueues[opid];
  }

  _nextOpId () {
    return `${this._qid}.${this._opid++}`;
  }

  _makeExecFn (fn, mode, opid) {
    if (this._locked) {
      throw new Error('Queue is locked for new operations!');
    }
    this._lastOpId = opid;
    this.isResolved = false;
    let timeout = mode === 'r' ? this.readTimeout : this.writeTimeout;
    return result => {
      if (this.debug) {
        console.log('>>>', mode, opid);
      }
      if (timeout) {
        var fnTimeout = setTimeout(() => {
          let error = `Promise queue [${mode}] operation has timed out!`;
          if (this.debug) {
            error = `${error} (opid: ${opid})`;
          }
          console.error(error);
          throw (error);
        }, timeout);
      }
      this.currentOpExec = [mode, opid];

      try {
        var executed = fn(result);
      } finally {
        this.currentOpExec = null;
      }

      let resolved = Promise.resolve(executed);
      if (this._subqueues[opid]) {
        this._subqueues[opid].lock();
      }
      resolved.then(() => {
        if (opid === this._lastOpId) {
          this.isResolved = true;
        }
        if ( this._subqueues[opid] ) {
          if (this._subqueues[opid].isResolved) {
            delete this._subqueues[opid];
          } else {
            throw new Error('Operation with subqueue has resolved before its subqueue has resolved!');
          }
        }
      });
      if (timeout) {
        resolved.then(() => clearTimeout(fnTimeout), () => clearTimeout(fnTimeout));
      }
      if (this.debug) {
        resolved.then(
          () => console.log('<<<', mode, opid),
          e => console.error('!!!', mode, opid, 'FAILED', e)
        );
      }
      return resolved;
    };
  }
}



// WEBPACK FOOTER //
// ./lib/rlink-locking/promise-queue.js